import React, { useState, useMemo } from 'react';
import { Filter, CircleCheckBig } from 'lucide-react';
import type { DigitalPassport, Passport, Pathway, Skillstree } from '@/types/achievement';
import AvailablePathways from './AvailablePathways';
import DigitalPassportsOverview from './DigitalPassportsOverview';
import SlideUpCard from '../SlideUpCard';
import PassportCard from './PassportCard';
import { useAchievements } from '@/hooks/useAchievements';
import { achievementsMapper } from '@/utils/achievementsMapper';

const PassportTab: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPassport, setSelectedPassport] = useState<DigitalPassport | null>(null);
  const [isCardOpen, setIsCardOpen] = useState(false);
  const { passports, loading, error, skillstree, coreCategories } = useAchievements();
  const stats = achievementsMapper.getPassportStats(passports);

  // Generate categories from API data using slugs
  const allCategories = useMemo(() => {
    const categories = [{ name: 'All Categories', slug: 'all' }];
    if (coreCategories && Array.isArray(coreCategories)) {
      coreCategories.forEach((category: any) => {
        if ((category.name && category.slug) || (category.core_category_name && category.core_category_slug)) {
          categories.push({
            name: category.name || category.core_category_name,
            slug: category.slug || category.core_category_slug
          });
        }
      });
    }
    return categories;
  }, [coreCategories]);

  // Filter ongoing pathways (incomplete passports - completed_at is null)
  const filteredOngoing = useMemo(() => {
    if (!passports || passports.length === 0) return [];

    // Get incomplete passports (completed_at is null)
    const incompletePassports = passports.filter(passport => 
      passport.completed_at === null
    );

    // Map incomplete passports to skillstree format for AvailablePathways
    let filtered = incompletePassports.map(passport => {
      const skillstreeData = skillstree?.find(
        tree => (tree as Skillstree).skillstree_id === ((passport as Passport).skillstree as Skillstree)?.id
      );
      
      if (skillstreeData) {
        return {
          ...skillstreeData,
          // Attach passport for progress calculation in PathwayCard
          associated_passport: passport,
          id: ((passport as Passport).skillstree as Skillstree)?.id,
          name: ((passport as Passport).skillstree as Skillstree)?.name,
          code: ((passport as Passport).skillstree as Skillstree)?.code,
        };
      }
      
      return null;
    }).filter(Boolean) as (Skillstree & { associated_passport?: DigitalPassport })[];

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(tree => 
        (tree as Skillstree).core_category_slug === selectedCategory
      );
    }

    return filtered;
  }, [passports, skillstree, selectedCategory]);

  // Filter completed passports and append category information
  const filteredPassports = useMemo(() => {
    if (!passports || passports.length === 0) return [];

    // Get completed passports (completed_at is not null)
    let filtered = passports.filter(passport => 
      passport.completed_at !== null
    );

    // Append category information to each completed passport
    const passportsWithCategory = filtered.map(passport => {
      const matchingSkillstree = skillstree?.find(
        tree => (tree as Skillstree).skillstree_id === ((passport as Passport).skillstree as Skillstree)?.id
      );
      
      if (matchingSkillstree) {
        return {
          ...passport,
          category_slug: (matchingSkillstree as Skillstree).core_category_slug,
          category_name: (matchingSkillstree as Skillstree).core_category_name,
          skillstree_details: matchingSkillstree
        };
      }
      
      return passport;
    });

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = passportsWithCategory.filter(passport => 
        passport.category_slug === selectedCategory
      ) as Passport[];
    } else {
      filtered = passportsWithCategory as Passport[];
    }

    return filtered;
  }, [passports, selectedCategory, skillstree]);

  // Debug logs to check if filtering is working
  // console.log('Selected Category:', selectedCategory);
  // console.log('All Skillstrees:', skillstree?.length);
  // console.log('Filtered Ongoing:', filteredOngoing.length);
  // console.log('Filtered Passports:', filteredPassports.length);

  const handlePassportClick = (passport: DigitalPassport) => {
    setSelectedPassport(passport);
    setIsCardOpen(true);
  };

  const handleCloseCard = () => {
    setIsCardOpen(false);
    // Small delay to allow animation to complete before clearing the passport
    setTimeout(() => setSelectedPassport(null), 300);
  };

  if (loading) return <div>Loading passports...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <>
      <section className="passport-tab space-y-8">
        <div className="flex flex-col items-center justify-center">
          <h2 className="text-xl text-white mb-2">SKILLSTURE Digital Microlearning Role Passport</h2>
          <p className="text-sm text-white/60 leading-relaxed">
            Verified digital credentials & downloadable certificates awarded upon completing entire Skillstrees. 
            Each passport confirms you have the foundational skills for a specific job role.
          </p>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:space-x-4">
          <div className="flex items-center space-x-4">
            <Filter size={18} className=" text-white/60" />
            <span className=" text-white/60 font-normal">Filter by category:</span>
          </div>
          <select 
            className="bg-black/20 border border-white/20 text-white/60 rounded-sm text-sm font-normal px-2 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-auto max-w-xs"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {allCategories.map((category, index) => (
              <option key={index} value={category.slug} className="bg-[#1a1a3a] py-1 px-2">
                {category.name}
              </option>
            ))}
          </select>
          <button 
            className="bg-white text-blue-900/80 px-2 py-1 rounded-sm text-sm font-normal transition-colors hover:bg-white/80 hover:text-blue-900 w-full md:w-auto"
            onClick={() => setSelectedCategory('all')}
          >
            Clear Filter
          </button>
        </div>

        {/* Ongoing Pathways Section */}
        <div>
          <AvailablePathways
            filteredPathways={filteredOngoing as Skillstree[]}
            onPassportClick={handlePassportClick}
          />
        </div>
        
        {/* Earned Passports Section */}
        <div>
          <h3 className="flex flex-row text-base font-normal text-white/90">
            <CircleCheckBig size={18} className="text-green-400 mt-1 me-2" />
            Earned Role Passports
          </h3>
          <DigitalPassportsOverview 
            filteredPassports={filteredPassports}
            setActiveTab={() => {}} 
            onPassportClick={handlePassportClick}
            showAll={true}
          />
        </div>
      </section>

      {/* Slide Up Card for Passport Details */}
      <SlideUpCard 
        isOpen={isCardOpen}
        onClose={handleCloseCard}
        title={selectedPassport?.role}
      >
        {selectedPassport && (
          <PassportCard passport={selectedPassport} />
        )}
      </SlideUpCard>
    </>
  );
};

export default PassportTab;